import {
  InputAdornment, MenuItem, TextField, makeStyles,
} from '@material-ui/core';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  useCallback, useEffect, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MaskedInput from 'react-text-mask';
import ReactInputMask from 'react-input-mask';
import { useAnalytics } from 'use-analytics';
import CardInfo from 'card-info';
import _ from 'lodash';
import { useRouter } from 'next/router';
import tw from 'twin.macro';
import { changeTrip, changeValidation } from '../../../../store/slices/trip';
import ErrorTooltip from '../../../ErrorTooltip';
import {
  CreditCardsFormValidation, CvcValidLength, DO_USE_VGS_INPUT_FORM, UserRolePermissions,
} from '../../../../constants';
import { availableStatesRequest, checkIsLoggedIn, checkSitePermission } from '../../../../store/slices/user';
import capitalize from '../../../../helpers/capitalize';
import validateCardNumber from '../../../../helpers/validationHelpers/validateCardNumber';
import Input from '../../../Input';
import useFocusOnError from '../../../../hooks/useFocusOnError';
import useStyles from './styles';
import Checkbox from '../../../Checkbox';
import getCurrentPageURL from '../../../../helpers/getCurrentPageURL';
import checkDoChargeAgencyPlanningFees from '../../../../helpers/selectors/checkDoChargeAgencyPlanningFees';
import CreditCardUsageType from '../../../../constants/CreditCardUsageType';
import checkIsPaymentRequiredForItinerary from '../../../../helpers/checkIsPaymentRequiredForItinerary';
import { usePayment } from './VgsIFrameInputProvider';
import VGSInputForm from './VGSInputForm';

const textareaInputStyles = makeStyles(({
  input: {
    '&': tw`font-body`,
  },
}));
const CardsForRequest = {
  visa: 'Visa',
  'master-card': 'MasterCard',
  'american-express': 'Amex',
  'diners-club': 'DinersClub',
  jcb: 'JCB',
  discover: 'Discover',
  unionpay: 'UnionPay',
};

const CreditCardMask = ({ inputRef, ...inputProps }) => (
  <ReactInputMask
    mask={inputProps.mask}
    maskPlaceholder={null}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...inputProps}
  />
);

CreditCardMask.propTypes = {
  inputRef: PropTypes.func.isRequired,
};

const SecurityCodeMask = ({ inputRef, ...inputProps }) => (
  <ReactInputMask
    mask={inputProps.mask}
    maskPlaceholder={null}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...inputProps}
  />
);

SecurityCodeMask.propTypes = {
  inputRef: PropTypes.func.isRequired,
};

const ExpirationDateMask = ({ inputRef, ...inputProps }) => (
  <MaskedInput
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...inputProps}
    ref={(ref) => {
      inputRef(ref ? ref.inputElement : null);
    }}
    mask={[/\d/, /\d/, '/', /\d/, /\d/]}
    placeholder="MM/YY"
    keepCharPositions
    guide={false}
  />
);

ExpirationDateMask.propTypes = {
  inputRef: PropTypes.func.isRequired,
};

const AddCardForm = ({
  tripId,
  cardIndex, doShowSecurityCodeInput, doTakeCardAddressInputs, widthClass,
  inputLayout, isCruise, useVGSInputForm,
}) => {
  const { track } = useAnalytics();
  const router = useRouter();
  const isLoggedIn = useSelector(checkIsLoggedIn);
  const [brandAlias, setBrandAlias] = useState('');
  const [srcIconCard, setSrcIconCard] = useState('');
  const [creditCardMask, setCreditCardMask] = useState('9999 9999 9999 9999');
  const [cvcMask, setCVCMask] = useState('999');
  const [nonValidCard, setNonValidCard] = useState(false);
  const doShowPaymentCheckboxes = router.query.doShowPaymentCheckboxes === 'true';
  const canSetDefaultCard = useSelector((state) => (doShowPaymentCheckboxes
    || checkSitePermission(UserRolePermissions.canSetDefaultPaymentMethod)(state)));
  const hasPermission = useSelector(checkSitePermission(UserRolePermissions.canSavePaymentMethod));

  const creditCards = useSelector((store) => store.trip.creditCards);
  const creditCard = creditCards && creditCards[cardIndex];
  const trip = useSelector((state) => state.trip.selectedTrips[tripId]);
  const canChargeAgencyPlanningFees = useSelector(checkDoChargeAgencyPlanningFees(tripId));
  const {
    passengers, agencyPlanningFees,
    doChargeAgencyPlanningFees,
  } = useSelector((store) => store.trip);

  const {
    availableCountries, availableStates,
    travelAgency, email: loggedInUserEmail,
  } = useSelector((store) => store.user);
  const useCardFromAgencyProfile = travelAgency && travelAgency.useCardFromAgencyProfile;

  const canSavePaymentMethod = !isCruise
    && (doShowPaymentCheckboxes || hasPermission)
    && trip?.doSupportsCardUpsertOperations;

  const ccNotValidFields = [
    creditCard.formValidations.cardHolderName,
    creditCard.formValidations.cardNumber,
    creditCard.formValidations.expiration,
    creditCard.formValidations.cvc,
  ].filter((item) => item);
  const isFrontInputsFilled = Boolean(creditCard.formValidations.cardHolderName
    || creditCard.formValidations.cardNumber
    || creditCard.formValidations.expiration);
  const isBackInputsFilled = Boolean(creditCard.formValidations.cvc
    || creditCard.formValidations.cardcode);
  const classes = useStyles({
    isFrontFilled: isFrontInputsFilled,
    isBackFilled: isBackInputsFilled,
  });
  const textareaInputClasses = textareaInputStyles();
  const dispatch = useDispatch();
  // VGS input form section
  const payment = usePayment(cardIndex);
  const {
    form, tokenizeFunc, errors, isFormValid,
  } = payment || {};
  const focusedOnErrorRef = useFocusOnError(isFormValid);

  const handleCardInfoChange = useCallback((field) => (event) => {
    const changeTripAction = {
      field: 'creditCards',
      value: {
        ...creditCards,
        [cardIndex]: {
          ...creditCard,
          [field]: event.target.type === 'checkbox' ? event.target.checked : event.target.value.replace(/^\s+/, ''),
        },
      },
    };

    const cardNumber = event.target.value.replace(/\D/g, '') || '';
    const cardNumberStringLength = cardNumber.length;
    if (field === 'cardNumber' || field === 'cvc') {
      _.set(changeTripAction, `value[${cardIndex}][${field}]`, cardNumber);
    }
    if (field === 'cardNumber' && cardNumberStringLength > 4) {
      const cardInfo = new CardInfo(cardNumber, {
        banksLogosPath: '/images/banks-logos/',
        brandsLogosPath: '/images/brands-logos/',
      });

      const rangeNumberLengths = (cardInfo.number.slice(0, 3) >= '300' && cardInfo.number.slice(0, 3) <= '305')
        || (cardInfo.number.slice(0, 4) <= '2720' && cardInfo.number.slice(0, 4) >= '2221') ? [16] : cardInfo.numberLengths;

      _.set(changeTripAction, `value[${cardIndex}].validCardNumberLengths`, rangeNumberLengths);
      _.set(changeTripAction, `value[${cardIndex}].validCvcLength`, cardInfo.codeLength || CvcValidLength);

      let isAlgorithmLuhned = true;
      // eslint-disable-next-line max-len
      const currentValidLength = rangeNumberLengths.find((validLength) => validLength === cardNumberStringLength);

      if (currentValidLength) {
        isAlgorithmLuhned = validateCardNumber(cardNumber);
      }
      setNonValidCard(!isAlgorithmLuhned);
      setSrcIconCard(isAlgorithmLuhned ? cardInfo.brandLogo : '');

      const nextCVCMask = new Array(cardInfo.codeLength || CvcValidLength).fill('9').join('');
      let nextCardMask = (cardInfo.numberMask || '9999 9999 9999 9999').replaceAll('0', '9');

      if ((cardInfo.number.slice(0, 3) >= '300' && cardInfo.number.slice(0, 3) <= '305')
        || (cardInfo.number.slice(0, 4) <= '2720' && cardInfo.number.slice(0, 4) >= '2221')) {
        nextCardMask = '9999 9999 9999 9999';
      }

      setCreditCardMask(nextCardMask);
      setCVCMask(nextCVCMask);

      if (cardInfo.number.slice(0, 4) <= 2720 && cardInfo.number.slice(0, 4) >= 2221) {
        setBrandAlias(CardsForRequest['master-card']);
        setSrcIconCard(isAlgorithmLuhned ? '/images/brands-logos/master-card-colored.svg' : '');
      } else {
        setBrandAlias(CardsForRequest[cardInfo.brandAlias] || cardInfo.brandName);
        setSrcIconCard(isAlgorithmLuhned ? cardInfo.brandLogo : '');
      }

      if (field === 'cardNumber' && creditCard.cvc) {
        const nextCVC = creditCard.cvc.slice(0, cardInfo.codeLength || CvcValidLength);
        _.set(changeTripAction, `value[${cardIndex}].cvc`, nextCVC);
      }
    }
    if (field === 'cardNumber' && cardNumberStringLength < 5) {
      setSrcIconCard('');
      setNonValidCard(false);
      setCVCMask('999');
      setCreditCardMask('9999 9999 9999 9999');
      _.set(changeTripAction, `value[${cardIndex}].cvc`, creditCard.cvc.slice(0, CvcValidLength));
      _.set(changeTripAction, `value[${cardIndex}].validCvcLength`, CvcValidLength);
      _.set(changeTripAction, `value[${cardIndex}].validCardNumberLengths`, [16]);
      setBrandAlias('Empty');
    }
    if (field === 'doSaveCard') {
      track('doSaveCreditCardToProfile', { value: !creditCard.doSaveCard, loggedInUserEmail, page: getCurrentPageURL() });
    }
    dispatch(changeTrip(changeTripAction));
  }, [cardIndex, creditCard, creditCards, dispatch, loggedInUserEmail, track]);

  const handleFormValidationsReset = useCallback(() => {
    if (creditCard.formValidations !== CreditCardsFormValidation) {
      dispatch(changeValidation({
        field: 'creditCards',
        targetIndex: cardIndex,
        formValidations: CreditCardsFormValidation,
      }));
    }
  }, [dispatch, cardIndex, creditCard]);

  useEffect(() => {
    if (brandAlias) {
      dispatch(changeTrip({
        field: 'creditCards',
        value: {
          ...creditCards,
          [cardIndex]: {
            ...creditCard,
            component: brandAlias || 'Empty',
          },
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardIndex, dispatch, brandAlias]);

  SecurityCodeMask.propTypes = {
    inputRef: PropTypes.func.isRequired,
  };

  const cvvInput = (
    <>
      <span className="w-full mr-3 text-gray-700">
        Security code*
      </span>
      <TextField
        onChange={handleCardInfoChange('cvc')}
        placeholder="CVC"
        InputProps={{
          inputComponent: SecurityCodeMask, classes: textareaInputClasses,
        }}
        // eslint-disable-next-line react/jsx-no-duplicate-props
        inputProps={{
          mask: cvcMask,
          className: 'inspectlet-sensitive',
        }}
        value={creditCard.cvc}
      />
    </>
  );

  useEffect(() => {
    if (creditCard.cardHolderName) {
      dispatch(changeTrip({
        field: 'creditCards',
        value: {
          ...creditCards,
          [cardIndex]: {
            ...creditCard,
            cardHolderName: creditCard.cardHolderName.toUpperCase(),
          },
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditCard.cardHolderName, cardIndex, dispatch]);

  useEffect(() => {
    if (creditCard.country) {
      dispatch(availableStatesRequest({ countryCode: creditCard.country }));
    }
  }, [creditCard.country, dispatch]);

  useEffect(() => {
    if (!availableStates) {
      dispatch(changeTrip({
        field: 'creditCards',
        value: {
          ...creditCards,
          [cardIndex]: {
            ...creditCard,
            city: '',
          },
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableStates, cardIndex, dispatch]);

  useEffect(() => {
    handleFormValidationsReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hasValidationErrors = nonValidCard
      || Object.values(creditCard.formValidations).some((validation) => validation !== null);
    if (hasValidationErrors && focusedOnErrorRef.current && !isFormValid) {
      focusedOnErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [nonValidCard, creditCard.formValidations, focusedOnErrorRef, isFormValid]);

  const doAnyHavePaidSeat = passengers && Object.values(passengers).some((passenger) => {
    if (!passenger || !passenger.travelerPreferences) return false;

    const seatMap = passenger.travelerPreferences.flightSeatNumbers;
    if (!seatMap || typeof seatMap !== 'object') return false;

    return Object.values(seatMap).some((seat) => seat && typeof seat.price === 'number' && seat.price > 0);
  });

  useEffect(() => {
    if (!trip) return;
    const newOptions = [
      ...(trip.cruiseItinerary ? [{
        value: trip.cruiseItinerary.tripSegmentId,
        label: 'Cruise',
      }] : []),
      ...(trip.flightItinerary ? [{
        value: trip.flightItinerary.tripSegmentId,
        label: 'Flight',
      }] : []),
      ...(trip.hotelItineraries ? Object.values(trip.hotelItineraries)
        .filter((h) => checkIsPaymentRequiredForItinerary(h)).map((itinerary) => ({
          value: itinerary.tripSegmentId,
          label: `Hotel - ${itinerary.hotel.name}`,
        })) : []),
      ...(trip.rentalCarItineraries ? Object.values(trip.rentalCarItineraries)
        .filter((h) => checkIsPaymentRequiredForItinerary(h)).map((itinerary) => ({
          value: itinerary.tripSegmentId,
          label: `Rental Car - ${itinerary.carDetails?.vehicle}`,
        })) : []),
      ...(canChargeAgencyPlanningFees && agencyPlanningFees > 0
        && doChargeAgencyPlanningFees && !trip.isUpdatingBookedTrip ? [{
          value: CreditCardUsageType.ServiceFees,
          label: 'Agency planning fee',
        }] : []),
      ...(doAnyHavePaidSeat ? [{
        value: CreditCardUsageType.FlightSeats,
        label: 'Seats',
      }] : []),
    ];

    dispatch(changeTrip({
      field: 'chargeableItemOptions',
      value: newOptions.filter(Boolean),
    }));
  }, [agencyPlanningFees, canChargeAgencyPlanningFees,
    dispatch, doAnyHavePaidSeat, doChargeAgencyPlanningFees, trip]);

  return (
    <div ref={focusedOnErrorRef}>
      <div onFocus={handleFormValidationsReset}>
        <div className="my-3">
          {useVGSInputForm ? (
            <VGSInputForm
              form={form}
              tokenizeFunc={tokenizeFunc}
              doShowSecurityCodeInput={doShowSecurityCodeInput}
              doTakeCardAddressInputs={doTakeCardAddressInputs}
              key={`VGSForm-${cardIndex}`}
              inputLayout={inputLayout}
              widthClass={widthClass}
              cardIndex={cardIndex}
              errors={errors}
              showErrorToolTip={Object.values(creditCard.formValidations).some(Boolean)}
            />
          ) : (
            <ErrorTooltip
              title={ccNotValidFields.length > 1 ? 'Card info should be filled' : (ccNotValidFields[0] || '')}
              open={isFrontInputsFilled || isBackInputsFilled}
              arrow
              placement="left"
            >
              <div className={clsx(classes.frontLayout, widthClass)}>
                <div className="col-span-5 flex flex-col">
                  <span className="text-gray-700">Cardholder Name*</span>
                  <TextField
                    placeholder="Full Name"
                    className="w-full"
                    onChange={handleCardInfoChange('cardHolderName')}
                    value={creditCard.cardHolderName}
                    InputProps={{ classes: textareaInputClasses }}
                  />
                </div>
                <div className={clsx(inputLayout === AddCardForm.InputLayoutTypes.COL ? 'col-span-3 flex flex-col' : 'col-span-5 flex flex-col')}>
                  <span className="text-gray-700">Credit/Debit Card Number*</span>
                  <ErrorTooltip
                    title="Please, enter valid credit card number"
                    open={nonValidCard}
                    arrow
                    PopperProps={{ disablePortal: true }}
                  >
                    <TextField
                      onChange={handleCardInfoChange('cardNumber')}
                      placeholder="Card Number"
                      InputProps={{
                        inputComponent: CreditCardMask,
                        classes: textareaInputClasses,
                        endAdornment: <InputAdornment position="end">{srcIconCard ? <img className="w-10 bg-gray-600" src={srcIconCard} alt="logo" /> : ''}</InputAdornment>,
                      }}
                      className="inline-block w-full inspectlet-sensitive"
                      value={creditCard.cardNumber}
                    // eslint-disable-next-line react/jsx-no-duplicate-props
                      inputProps={{
                        mask: creditCardMask,
                        className: 'inspectlet-sensitive',
                      }}
                    />
                  </ErrorTooltip>
                </div>
                <div className={clsx(inputLayout === AddCardForm.InputLayoutTypes.COL ? 'col-span-2 flex flex-col' : 'col-span-5 flex flex-col')}>
                  <span className="text-gray-700">Expiration Date*</span>
                  <TextField
                    className="w-4/12 inline-block w-full inspectlet-sensitive"
                    onChange={handleCardInfoChange('expiration')}
                    InputProps={{
                      inputComponent: ExpirationDateMask, classes: textareaInputClasses,
                    }}
                    value={creditCard.expiration}
                  // eslint-disable-next-line react/jsx-no-duplicate-props
                    inputProps={{
                      className: 'inspectlet-sensitive',
                    }}
                  />
                </div>
                {doShowSecurityCodeInput && inputLayout === AddCardForm.InputLayoutTypes.ROW && (
                  <div className="col-span-5 flex flex-col">
                    {cvvInput}
                  </div>
                )}
              </div>
            </ErrorTooltip>
          )}
          {doShowSecurityCodeInput && inputLayout === AddCardForm.InputLayoutTypes.COL
           && !useVGSInputForm ? (
             <div className={classes.backLayout}>
               <div className="h-12 bg-theme bg-opacity-10
            col-span-2 justify-self-stretch self-center"
               />
               <div className="flex flex-col items-end
            col-start-2 items-baseline ml-2"
               >
                 {cvvInput}
               </div>
             </div>
            ) : <></>}
        </div>
        {(doTakeCardAddressInputs || (isLoggedIn && canSavePaymentMethod)
         || (isLoggedIn && useCardFromAgencyProfile && canSetDefaultCard)) && (
         <div className={clsx('grid grid-cols-4 gap-4 my-6', { 'mt-8': doShowSecurityCodeInput, 'mt-3': !doShowSecurityCodeInput })}>
           {doTakeCardAddressInputs && (
           <>
             <div className={clsx('col-span-4', { 'sm:col-span-2': inputLayout === AddCardForm.InputLayoutTypes.COL })}>
               <span className="text-gray-700 sm:ml-6">
                 Address line 1*
               </span>
               <ErrorTooltip
                 title={creditCard.formValidations.addressLineOne || ''}
                 open={creditCard.formValidations.addressLineOne !== null}
                 arrow
               >
                 <div className="w-full">
                   <Input
                     type="text"
                     onChange={handleCardInfoChange('addressLineOne')}
                     value={creditCard.addressLineOne}
                   />
                 </div>
               </ErrorTooltip>
             </div>
             <div className={clsx('col-span-4', { 'sm:col-span-2': inputLayout === AddCardForm.InputLayoutTypes.COL })}>
               <span className="text-gray-700 sm:ml-6">
                 Address line 2
               </span>
               <Input
                 type="text"
                 onChange={handleCardInfoChange('addressLineTwo')}
                 value={creditCard.addressLineTwo}
               />
             </div>
             <div className={clsx('col-span-4', { 'sm:col-span-2': inputLayout === AddCardForm.InputLayoutTypes.COL })}>
               <span className="text-gray-700 sm:ml-6">
                 City*
               </span>
               <ErrorTooltip
                 title={creditCard.formValidations.city || ''}
                 open={creditCard.formValidations.city !== null}
                 arrow
               >
                 <div className="w-full">
                   <Input
                     type="text"
                     onChange={handleCardInfoChange('city')}
                     value={creditCard.city}
                   />
                 </div>
               </ErrorTooltip>
             </div>
             <div className={clsx('col-span-4', { 'sm:col-span-2': inputLayout === AddCardForm.InputLayoutTypes.COL })}>
               <span className="text-gray-700 sm:ml-6">
                 Billing ZIP code*
               </span>
               <ErrorTooltip
                 title={creditCard.formValidations.billingCode || ''}
                 open={creditCard.formValidations.billingCode !== null}
                 arrow
               >
                 <div className="w-full">
                   <Input
                     dataTestId="billingZipCode"
                     type="text"
                     onChange={handleCardInfoChange('billingCode')}
                     value={creditCard.billingCode}
                   />
                 </div>
               </ErrorTooltip>
             </div>
             <div className={clsx('col-span-4', { 'sm:col-span-2': inputLayout === AddCardForm.InputLayoutTypes.COL })}>
               <span className="text-gray-700 sm:ml-6">
                 Country*
               </span>
               <ErrorTooltip
                 title={creditCard.formValidations.country || ''}
                 open={creditCard.formValidations.country !== null}
                 arrow
               >
                 <TextField
                   select
                   variant="filled"
                   value={creditCard.country}
                   onChange={handleCardInfoChange('country')}
                   placeholder="Country*"
                   className={`px-6 py-3 rounded-2xl bg-gray-500 bg-opacity-5
                placeholder-gray-700 w-full ${classes.root}`}
                   InputProps={{ classes: textareaInputClasses }}
                 >
                   {availableCountries && availableCountries.map((option) => (
                     <MenuItem value={option.code} key={option.code}>
                       {capitalize(option.name)}
                     </MenuItem>
                   ))}
                 </TextField>
               </ErrorTooltip>
             </div>

             <div className={clsx('col-span-4 sm:block', { 'sm:col-span-2': inputLayout === AddCardForm.InputLayoutTypes.COL, hidden: !creditCard.country || !availableStates.length })}>
               {creditCard.country
                && availableStates.length > 0
                && (
                  <div>
                    <span className="text-gray-700 sm:ml-6">
                      State*
                    </span>
                    <ErrorTooltip
                      title={creditCard.formValidations.state || ''}
                      open={creditCard.formValidations.state !== null}
                      arrow
                    >
                      <TextField
                        select
                        variant="filled"
                        value={creditCard.state}
                        onChange={handleCardInfoChange('state')}
                        placeholder="State"
                        className={`px-6 py-3 rounded-2xl bg-gray-500 bg-opacity-5
                    placeholder-gray-700 w-full ${classes.root} ${clsx({ 'bg-opacity-100': !creditCard.country || !availableStates.length })}`}
                        InputProps={{ classes: textareaInputClasses }}
                      >
                        {availableStates.length
                          ? availableStates.map((option) => (
                            <MenuItem key={option.code} value={option.code}>
                              {capitalize(option.name)}
                            </MenuItem>
                          ))
                          : []}
                      </TextField>
                    </ErrorTooltip>
                  </div>
                )}
             </div>
           </>
           )}
           {isLoggedIn && creditCard.doSaveCard && canSavePaymentMethod && (
           <div className="col-span-4">
             <span className={clsx('text-gray-700 text-sm', { 'sm:ml-6': doTakeCardAddressInputs, 'sm:ml-4': !doTakeCardAddressInputs })}>
               Additional Information
             </span>
             <div className="w-full">
               <Input
                 dataTestId="cardInfoText"
                 type="text"
                 onChange={handleCardInfoChange('informationText')}
                 value={creditCard.informationText}
               />
             </div>
           </div>
           )}
           {isLoggedIn && (
           <div className="col-span-4">
             {useCardFromAgencyProfile && canSetDefaultCard
               ? (
                 <Checkbox
                   checked={creditCard.doSetAsDefault || false}
                   name="doSetAsDefault"
                   onChange={handleCardInfoChange('doSetAsDefault')}
                   value={creditCard.doSetAsDefault}
                   labelFontBold
                   label="Set as default for payment"
                 />
               ) : <></>}

             {canSavePaymentMethod ? (
               <>
                 <Checkbox
                   checked={creditCard.doSaveCard || false}
                   name="doSaveCard"
                   onChange={handleCardInfoChange('doSaveCard')}
                   value={creditCard.doSaveCard}
                   labelFontBold
                   label="Save Credit Card To Profile"
                   disabled={isCruise}
                 />
                 <div className="text-gray-700">
                   {useCardFromAgencyProfile
                     ? (
                       <span>
                         (Note: Credit card will be saved to
                         {' '}
                         {(travelAgency || {}).sabreProfileName || 'sabre'}
                         {' '}
                         profile)
                       </span>
                     )
                     : <span>(Note: Credit card will be saved to Passenger 1&apos;s profile)</span>}
                 </div>
               </>
             ) : <></>}
           </div>
           )}
         </div>
        )}
      </div>
    </div>
  );
};

AddCardForm.InputLayoutTypes = {
  COL: 0,
  ROW: 1,
};

AddCardForm.propTypes = {
  tripId: PropTypes.string.isRequired,
  cardIndex: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  doShowSecurityCodeInput: PropTypes.bool,
  doTakeCardAddressInputs: PropTypes.bool,
  widthClass: PropTypes.string,
  labelClasses: PropTypes.shape({}),
  inputLayout: PropTypes.number, // column or row
  isCruise: PropTypes.bool,
  useVGSInputForm: PropTypes.bool,
};

AddCardForm.defaultProps = {
  doShowSecurityCodeInput: true,
  doTakeCardAddressInputs: true,
  widthClass: 'w-full sm:w-1/2',
  labelClasses: {},
  inputLayout: AddCardForm.InputLayoutTypes.COL,
  isCruise: false,
  useVGSInputForm: DO_USE_VGS_INPUT_FORM,
};

export default AddCardForm;
